<template>
  <div class="modal">
    <div class="background-overlay"></div>
    <div class="message-container">
      <div @click="$emit('close')" class="modal-close">
        <CloseIcon />
      </div>
      <div class="modal-content">
        <div class="top">
          <div class="title">
            <span class="card-title">Message your fans</span>
            <p class="card-text">
              Send an SMS to your fans.
            </p>
          </div>
        </div>
        <div class="recipients-selector" v-if="!isRelease">
          <label for="world">
            <input
              type="radio"
              id="world"
              name="location"
              value="world"
              v-model="location"
            />
            <span>
              Worldwide ({{ fans.world }} fans) - ${{
                fans | formatCost("world")
              }}
            </span>
          </label>
          <label for="us_canada">
            <input
              type="radio"
              id="us_canada"
              name="location"
              value="us_canada"
              v-model="location"
            />
            <span>
              US & Canada ({{ fans.us_canada }} fans) - ${{
                fans | formatCost("us_canada")
              }}
            </span>
          </label>
        </div>
        <div class="message-input">
          <textarea
            v-model="message"
            placeholder="Type your message here..."
            rows="5"
            cols="50"
          ></textarea>
          <input
            type="text"
            class="link-input"
            placeholder="Add a link to your message"
            v-model="link"
          />
          <div class="sms-segments">
            <template v-if="messageCount.messages > 1">
              <div
                v-for="i in messageCount.messages - 1"
                :key="i"
                class="circular-progress"
              >
                <div class="progress-value">
                  {{ messageCount.characterPerMessage }} /
                  {{ messageCount.characterPerMessage }}
                </div>
              </div>
            </template>
            <div class="circular-progress" :style="progressStyle">
              <div class="progress-value">
                {{ messageCount.inCurrentMessage }} /
                {{ messageCount.characterPerMessage }}
              </div>
            </div>
            <span class="count" v-if="messageCount.messages > 1">
              SMS Segments: {{ messageCount.messages }}
            </span>
          </div>
        </div>
        <div class="buttons" v-if="!scheduling && !editingMessage">
          <button
            class="button-default"
            @click="scheduling = true"
            :disabled="!message"
            v-if="!submitting"
          >
            <font-awesome-icon :icon="['far', 'clock']" />
            Schedule
          </button>
          <button
            class="button-default"
            @click="
              () => submit({ send_at: null, message, location, sms_link: link })
            "
            :disabled="submitting || !message || !link"
          >
            <font-awesome-icon :icon="['far', 'paper-plane']" />
            {{ submitting ? "Submitting..." : "Send Now" }}
          </button>
        </div>
        <div class="send-at" v-if="scheduling || isRelease">
          <span class="title" v-if="!isRelease">Send date</span>
          <div class="inputs" v-if="!isRelease">
            <div class="input-box date">
              <input
                id="date-picker"
                type="date"
                v-model="send_at.date"
                :min="today"
              />
              <CalendarIcon class="calendar-icon" />
            </div>
            <TimeInput v-model="send_at.time" />
            <div
              @click="$refs.select.click()"
              class="input-box timezone-selector"
            >
              <select
                ref="select"
                v-model="send_at.timezone"
                class="standard-select"
              >
                <option
                  v-for="(timezone, i) in timezones"
                  :value="timezone"
                  :key="i"
                >
                  {{ timezone }}
                </option>
              </select>
              <ChevronDownIcon class="pointer-events-none" />
            </div>
          </div>
          <div class="buttons" v-if="!editingMessage">
            <button
              class="button-default"
              @click="
                () => submit({ send_at, message, location, sms_link: link })
              "
              :disabled="
                submitting ||
                  !send_at.date ||
                  !send_at.time ||
                  !message ||
                  !link
              "
            >
              <font-awesome-icon :icon="['far', 'clock']" />
              {{ submitting ? "Submitting..." : "Schedule" }}
            </button>
            <button
              class="button-secondary"
              @click="scheduling = false"
              v-if="!submitting"
            >
              Cancel
            </button>
          </div>
          <div class="buttons" v-if="editingMessage">
            <button
              class="button-default"
              @click="
                () =>
                  submit({
                    send_at: scheduling ? send_at : null,
                    message,
                    location,
                    sms_link: link,
                  })
              "
              :disabled="submitting || !message || !link"
            >
              {{ submitting ? "Submitting..." : "Update" }}
            </button>
            <button
              class="button-secondary"
              @click="() => $emit('close')"
              v-if="!submitting"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { count } from "sms-length";

import CloseIcon from "../../../public/img/icons/close.svg";
import CalendarIcon from "../../../public/img/icons/calendar.svg";
import ChevronDownIcon from "../../../public/img/icons/chevron-down.svg";
import "@/assets/scss/custom/vendors-extensions/_sweetalert.scss";
import TimeInput from "@/components/Custom/TimeInput";

const getInitialState = () => ({
  location: "world",
  message: "",
  link: "",
  scheduling: false,
  isRelease: false,
  send_at: {
    date: "",
    time: "12:00 pm",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
});

export default {
  name: "BulkMessageInput",
  components: {
    CloseIcon,
    CalendarIcon,
    ChevronDownIcon,
    TimeInput,
  },
  props: ["fans", "submitting", "editingMessage"],
  data() {
    return {
      ...getInitialState(),
    };
  },
  mounted() {
    const messageObject = this.editingMessage;

    if (messageObject) {
      Object.assign(this, {
        location: messageObject.location,
        message: messageObject.message,
        isRelease: !!messageObject.link_destination,
        scheduling: messageObject.send_at ? true : false,
        link: messageObject.sms_link,
        send_at: {
          date: moment(messageObject.send_at).format("YYYY-MM-DD"),
          time: moment(messageObject.send_at).format("hh:mm a"),
          timezone: messageObject.timezone,
        },
      });
    } else {
      Object.assign(this, getInitialState());
    }
  },
  computed: {
    artist() {
      return this.$store.getters.getArtist;
    },
    timezones() {
      return Intl.supportedValuesOf("timeZone");
    },
    today() {
      let today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yyyy = today.getFullYear();
      today = yyyy + "-" + mm + "-" + dd;
      return today;
    },
    messageCount() {
      let fullMessage = this.message;
      if (this.link) fullMessage += ` ${this.link}`;
      return count(fullMessage || "");
    },
    progressStyle() {
      return {
        background: `conic-gradient(#8786ab 
        ${(this.messageCount.inCurrentMessage /
          this.messageCount.characterPerMessage) *
          360}deg, #312f62 0deg)`,
      };
    },
  },
  methods: {
    async submit(data) {
      await this.$emit(
        "submit",
        this.editingMessage
          ? { ...data, id: this.editingMessage.id }
          : { ...data }
      );
      Object.assign(this, getInitialState());
    },
  },
  filters: {
    formatCost(fans, location) {
      const costUsCanada = fans.us_canada * 0.01;
      const costWorldRest = (fans.world - fans.us_canada) * 0.05;
      const cost =
        location === "us_canada" ? costUsCanada : costWorldRest + costUsCanada;
      return cost.toFixed(2);
    },
  },
};
</script>
<style lang="scss" scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99;

  .message-container {
    margin: 15px;
    padding: 60px 0 0;
    width: 680px;
    z-index: 98;
    background: linear-gradient(
      241.25deg,
      rgba(41, 39, 85, 0.45) 4.4%,
      rgba(41, 39, 84, 0.85) 61.77%,
      rgba(27, 24, 66, 0.45) 119.94%
    );
    backdrop-filter: blur(23px);
    border-radius: 10px;

    .modal-close {
      cursor: pointer;
      position: absolute;
      top: 18px;
      right: 18px;
      display: flex;
      flex-direction: column;

      &:hover {
        svg {
          path {
            fill: white;
          }
        }

        .close-text {
          color: white;
        }
      }

      svg {
        width: 24px;

        path {
          fill: #8786ab;
        }
      }
    }

    .modal-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0 40px 40px;

      .top {
        display: flex;
        justify-content: space-between;
        width: 100%;

        .title {
          display: flex;
          flex-direction: column;

          .card-title {
            font-size: 18px;
            font-weight: 600;
            color: white;
          }

          .card-text {
            font-size: 14px;
            font-weight: 400;
            color: $text-type-0;
            margin-bottom: 30px;
          }
        }
      }

      .recipients-selector {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        width: 100%;

        label {
          font-size: 14px;
          font-weight: 400;
          padding: 20px 10px;
          background: $bg-type-1;
          border: 1px solid #e5e5e5;
          border-radius: 5px;

          input[type="radio"] {
            margin-right: 10px;
          }

          span {
            color: #fff;
          }

          &:first-child {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
          }

          &:last-child {
            border-top: none;
            border-top-left-radius: 0;
            border-top-right-radius: 0;
          }

          &:hover {
            background: $bg-type-2;
            cursor: pointer;
          }
        }
      }

      .message-input {
        width: 100%;
        margin-bottom: 20px;

        textarea {
          width: 100%;
          height: 100px;
          border-radius: 5px;
          border: 1px solid #e5e5e5;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          padding: 10px;
          font-size: 14px;
          font-weight: 400;
          background: $bg-type-1;
          color: #fff;
          outline: none;
          resize: none;
        }

        .link-input {
          width: 100%;
          height: 57px;
          border: 1px solid #e5e5e5;
          border-top: none;
          padding: 10px;
          font-size: 14px;
          font-weight: 400;
          background: $bg-type-1;
          color: #fff;
          margin-top: -3px;
          outline: none;
        }

        .sms-segments {
          margin-top: -3px;
          display: flex;
          align-items: center;
          background-color: #1b193c;
          border-radius: 5px;
          border: 1px solid #e5e5e5;
          border-top: none;
          border-top-left-radius: 0;
          border-top-right-radius: 0;
          padding: 8px 10px;

          .count {
            font-size: 14px;
            font-weight: 400;
            color: #8786ab;
            margin-left: auto;
          }

          .circular-progress {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-right: 10px;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: conic-gradient(#8786ab 3600deg, #312f62 0deg);

            &::before {
              content: "";
              position: absolute;
              width: 35px;
              height: 35px;
              border-radius: 50%;
              background-color: #1b193c;
              clip-path: polygon(
                50% 0%,
                50% 100%,
                100% 100%,
                100% 0%,
                0% 0%,
                0% 100%,
                50% 100%,
                50% 0%
              );
            }

            .progress-value {
              font-size: 8px;
              position: relative;
              color: #8786ab;
            }
          }
        }
      }

      .buttons {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        height: 40px;
        width: 100%;

        button {
          margin-left: 10px;

          &:first-child {
            margin-left: 0;
          }

          svg {
            margin: 0;
            margin-right: 5px;
          }
        }
      }

      .send-at {
        margin-top: 30px;

        .title {
          font-size: 0.9rem;
          font-weight: 900;
          color: #8786ab;
        }

        .inputs {
          display: flex;
          align-items: baseline;
          width: 620px;

          .input-box {
            margin-bottom: 24px;
            margin-top: 10px;
            padding: 0 10px;
            display: flex;
            align-items: center;
            background: $purple-dark;
            border: 1px solid #312f62;
            border-radius: 5px;

            input {
              z-index: 99;
              background: $purple-dark;
              flex: 1;
              padding: 12px 0;
              color: $text-type-2;
              border: none;
              /* 14/Regular */
              font-size: 14px;
              line-height: 17px;
              color: #b1afcd;

              &:focus {
                outline: none;
                border: none;
              }

              &::placeholder {
                color: $text-type-2;
                /* 14/Regular */
                font-size: 14px;
                line-height: 17px;
              }
            }
          }

          .date {
            position: relative;
            width: 480px;

            input {
              color: #b1afcd;
              background: transparent;
            }

            input::-webkit-calendar-picker-indicator {
              background: transparent;
            }

            .calendar-icon {
              position: absolute;
              z-index: 99;
              right: 10px;
              pointer-events: none;
              height: 24px;
              width: 24px;

              path {
                fill: $text-type-2;
              }
            }
          }

          .time {
            margin-top: 10px;
            margin-left: 20px;
            width: 115px;
            overflow: hidden;

            input {
              &::-webkit-calendar-picker-indicator {
                background: transparent;
              }
            }
          }

          .timezone-selector {
            margin-left: 10px;
            display: flex;
            position: relative;

            select {
              padding-right: 0;
              padding-left: 0;
              margin-top: 0;
              border: 0;
            }

            svg {
              background: $purple-dark;
              width: 20px;
              right: 10px;
              top: 10px;
              position: absolute;
              z-index: 10;

              path {
                stroke: #59588d;
              }
            }
          }
        }
      }
    }

    .modal-footer {
      margin-top: 30px;
      padding: 30px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-top: 1px solid #b1afcd;

      .price {
        display: flex;
        align-items: flex-end;

        .price-value {
          font-weight: 700;
          font-size: 30px;
          line-height: 37px;
          color: #ffffff;
        }

        .price-period {
          font-weight: 400;
          font-size: 18px;
          line-height: 22px;
          color: #b1afcd;
          margin-left: 10px;
        }
      }

      button {
        padding: 9px 40px;
      }
    }
  }

  .background-overlay {
    z-index: 97;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: unset;
    /* Purple-dark */
    background: $purple-dark;
    opacity: 0.9;
    webkit-backdrop-filter: blur(10px) !important;
    backdrop-filter: blur(10px) !important;
  }
}
</style>
